import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Stack,
  IconButton,
  Tooltip,
  Divider,
  Chip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PrintIcon from '@mui/icons-material/Print';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PersonIcon from '@mui/icons-material/Person';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import HistoryIcon from '@mui/icons-material/History';

const ClinicalDataCard = ({
  title,
  subtitle,
  date,
  type = 'visit', // 'visit', 'operation', 'medication', 'history'
  content = [],
  status,
  statusColor = 'default',
  onEdit,
  onDelete,
  onPreview,
  onPrint,
}) => {
  const getIcon = () => {
    switch (type) {
      case 'operation':
        return <LocalHospitalIcon fontSize="small" color="primary" />;
      case 'medication':
        return <MedicalServicesIcon fontSize="small" color="error" />;
      case 'history':
        return <HistoryIcon fontSize="small" color="secondary" />;
      default:
        return <CalendarTodayIcon fontSize="small" color="primary" />;
    }
  };

  return (
    <Card 
      variant="outlined" 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          borderColor: 'primary.main',
        }
      }}
    >
      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        <Stack spacing={1.5}>
          {/* Header */}
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Stack direction="row" spacing={1} alignItems="center">
              {getIcon()}
              <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>
                {date}
              </Typography>
            </Stack>
            {status && (
              <Chip 
                label={status} 
                size="small" 
                color={statusColor} 
                sx={{ height: 20, fontSize: '0.675rem', fontWeight: 700, textTransform: 'uppercase' }} 
              />
            )}
          </Stack>

          <Box>
            <Typography variant="h6" component="div" sx={{ fontWeight: 700, lineHeight: 1.2, mb: 0.5 }}>
              {title || 'No Title'}
            </Typography>
            {subtitle && (
              <Stack direction="row" spacing={0.5} alignItems="center">
                <PersonIcon sx={{ fontSize: '0.875rem', color: 'text.secondary' }} />
                <Typography variant="body2" color="text.secondary">
                  {subtitle}
                </Typography>
              </Stack>
            )}
          </Box>

          <Divider />

          {/* Content Snippets */}
          <Stack spacing={1}>
            {content.map((item, index) => (
              item.value && (
                <Box key={index}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', textTransform: 'uppercase', display: 'block', mb: 0.25 }}>
                    {item.label}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      display: '-webkit-box', 
                      WebkitLineClamp: 2, 
                      WebkitBoxOrient: 'vertical', 
                      overflow: 'hidden',
                      lineHeight: 1.4
                    }}
                  >
                    {item.value}
                  </Typography>
                </Box>
              )
            ))}
          </Stack>
        </Stack>
      </CardContent>

      <Divider />
      
      <CardActions sx={{ justifyContent: 'flex-end', px: 1, py: 0.5, bgcolor: 'rgba(0,0,0,0.02)' }}>
        <Stack direction="row" spacing={0.5}>
          {onPreview && (
            <Tooltip title="Preview">
              <IconButton size="small" onClick={onPreview} color="primary">
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onPrint && (
            <Tooltip title="Print">
              <IconButton size="small" onClick={onPrint} color="info">
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip title="Edit">
              <IconButton size="small" onClick={onEdit} color="secondary">
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {onDelete && (
            <Tooltip title="Delete">
              <IconButton size="small" onClick={onDelete} color="error">
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
      </CardActions>
    </Card>
  );
};

export default ClinicalDataCard;
